<template>
  <VaSidebar v-model="writableVisible" :width="sidebarWidth" :color="color" minimized-width="0">
    <VaAccordion v-model="value" multiple>
      <VaCollapse v-for="(route, index) in navigationRoutes.routes" :key="index">
        <template #header="{ value: isCollapsed }">
          <VaSidebarItem
            v-if="!route.href"
            :to="route.children ? undefined : { name: route.name }"
            :active="routeHasActiveChild(route)"
            :active-color="activeColor"
            :text-color="textColor(route)"
            :aria-label="`${route.children ? 'Open category ' : 'Visit'} ${t(route.displayName)}`"
            role="button"
            hover-opacity="0.10"
          >
            <VaSidebarItemContent class="sidebar-item-content">
              <div class="flex items-center gap-3 flex-1">
                <VaIcon
                  v-if="route.meta.icon"
                  aria-hidden="true"
                  :name="route.meta.icon"
                  size="20px"
                  :color="iconColor(route)"
                  class="sidebar-icon"
                />
                <VaSidebarItemTitle class="sidebar-title">
                  {{ t(route.displayName) }}
                </VaSidebarItemTitle>
              </div>
              <VaIcon
                v-if="route.children"
                :name="arrowDirection(isCollapsed)"
                size="16px"
                :color="iconColor(route)"
                class="sidebar-arrow"
              />
            </VaSidebarItemContent>
          </VaSidebarItem>
          <VaSidebarItem
            v-else
            :href="route.href"
            target="_blank"
            :active-color="activeColor"
            :text-color="textColor(route)"
            :aria-label="`Visit ${t(route.displayName)}`"
            hover-opacity="0.10"
          >
            <VaSidebarItemContent class="sidebar-item-content">
              <div class="flex items-center gap-3 flex-1">
                <VaIcon
                  v-if="route.meta.icon"
                  aria-hidden="true"
                  :name="route.meta.icon"
                  size="20px"
                  :color="iconColor(route)"
                  class="sidebar-icon"
                />
                <VaSidebarItemTitle class="sidebar-title">
                  {{ t(route.displayName) }}
                </VaSidebarItemTitle>
              </div>
            </VaSidebarItemContent>
          </VaSidebarItem>
        </template>
        <template #body>
          <div v-for="(childRoute, index2) in route.children" :key="index2">
            <VaSidebarItem
              :to="{ name: childRoute.name }"
              :active="isActiveChildRoute(childRoute)"
              :active-color="activeColor"
              :text-color="textColor(childRoute)"
              :aria-label="`Visit ${t(route.displayName)}`"
              hover-opacity="0.10"
            >
              <VaSidebarItemContent class="sidebar-item-content sidebar-item-content-child">
                <VaSidebarItemTitle class="sidebar-title sidebar-title-child">
                  {{ t(childRoute.displayName) }}
                </VaSidebarItemTitle>
              </VaSidebarItemContent>
            </VaSidebarItem>
          </div>
        </template>
      </VaCollapse>
    </VaAccordion>
  </VaSidebar>
</template>
<script lang="ts">
import { defineComponent, watch, ref, computed } from 'vue'
import { useRoute } from 'vue-router'

import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'

import navigationRoutes, { type INavigationRoute } from './NavigationRoutes'

export default defineComponent({
  name: 'Sidebar',
  props: {
    visible: { type: Boolean, default: true },
    mobile: { type: Boolean, default: false },
  },
  emits: ['update:visible'],

  setup: (props, { emit }) => {
    const { getColor, colorToRgba } = useColors()
    const route = useRoute()
    const { t } = useI18n()

    const { visible, mobile } = props

    const value = ref<boolean[]>([])

    const writableVisible = computed({
      get: () => visible,
      set: (v: boolean) => emit('update:visible', v),
    })

    const isActiveChildRoute = (child: INavigationRoute) => route.name === child.name

    const routeHasActiveChild = (section: INavigationRoute) => {
      if (!section.children) {
        return route.path.endsWith(`${section.name}`)
      }

      return section.children.some(({ name }) => route.path.endsWith(`${name}`))
    }

    const setActiveExpand = () =>
      (value.value = navigationRoutes.routes.map((route: INavigationRoute) => routeHasActiveChild(route)))

    const sidebarWidth = computed(() => (mobile ? '100vw' : '280px'))
    const color = computed(() => getColor('background-secondary'))
    const activeColor = computed(() => colorToRgba(getColor('focus'), 0.1))

    const iconColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'secondary')
    const textColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'textPrimary')
    const arrowDirection = (state: boolean) => (state ? 'va-arrow-up' : 'va-arrow-down')

    watch(() => route.fullPath, setActiveExpand, { immediate: true })

    return {
      writableVisible,
      sidebarWidth,
      value,
      color,
      activeColor,
      navigationRoutes,
      routeHasActiveChild,
      isActiveChildRoute,
      t,
      iconColor,
      textColor,
      arrowDirection,
    }
  },
})
</script>

<style lang="scss" scoped>
.sidebar-item-content {
  padding: 0.875rem 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s ease;
  border-radius: 0.375rem;
  margin: 0.25rem 0.5rem;

  &:hover {
    background-color: var(--va-background-element);
  }
}

.sidebar-item-content-child {
  padding-left: 3rem;
  margin-left: 0.5rem;
  margin-right: 0.5rem;
}

.sidebar-icon {
  flex-shrink: 0;
  transition: color 0.2s ease;
}

.sidebar-title {
  font-size: 0.9375rem;
  font-weight: 500;
  line-height: 1.5;
  color: inherit;
  transition: color 0.2s ease;
  flex: 1;
}

.sidebar-title-child {
  font-size: 0.875rem;
  font-weight: 400;
}

.sidebar-arrow {
  flex-shrink: 0;
  transition:
    transform 0.2s ease,
    color 0.2s ease;
  margin-left: 0.5rem;
}

:deep(.va-sidebar-item[aria-current='page']),
:deep(.va-sidebar-item--active) {
  .sidebar-item-content {
    background-color: var(--va-background-element);

    .sidebar-icon,
    .sidebar-title {
      color: var(--va-primary);
    }
  }
}

:deep(.va-sidebar-item:hover) {
  .sidebar-item-content {
    background-color: var(--va-background-element);
  }
}
</style>
